import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {BaseWidgetComponent} from "../common/BaseWidget";
import {MultiChartWidgetOptions} from "./MultiChartWidgetOptions";
import {TypeOfDateSettingsEnumCharts} from "../../../enums/dashboard/TypeOfDateSettingsEnumCharts";
import {WidgetButton} from "../common/WidgetButton";
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {UserMetricGroup} from "../../../models/user/Metric/UserMetricGroup";
import {UserMetricInstance} from "../../../models/user/Metric/UserMetricInstance";
import {ChartFormEditComponent} from "../../charts/chart-form/chart-form-drawer/chart-form-edit.component";
import {GetUserMetricGroupCB} from "../../../ope/user/metric/GetUserMetricGroupCB";
import {UserOpeService} from "../../../services/ope/userOpe.service";
import * as uuid from "uuid";
import {DashboardWidget} from "../../../models/dashboard/DashboardWidget";
import {ChartsComponent} from "../../charts/charts.component";
import {DxTabPanelComponent} from "devextreme-angular";
import {
  ChartTimeSelectorComponent
} from "../../topbar/chart-controls/chart-time-selector/chart-time-selector.component";

@Component({
  selector: 'app-multi-chart-widget',
  templateUrl: './multi-chart-widget.component.html',
  styleUrls: ['./multi-chart-widget.component.css', '../../common/widget-style.css', '../../charts/charts.component.css']
})
export class MultiChartWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy, AfterViewInit {

  /**
   * Edit mode on/off
   */
  @Input() editMode = false;
  @Input() widgetOptions: any;
  /**
   * remove widget event
   */
  @Output() removeItem = new EventEmitter();
  /**
   * edit widget event
   */
  @Output() editItem = new EventEmitter<MultiChartWidgetOptions>();
  @ViewChildren('chartsComponent') chartsComponents!: QueryList<ChartsComponent>;
  @ViewChild('tabPanelComponent', {static: false}) tabPanelComponent!: DxTabPanelComponent;
  @ViewChild('chartTimeSelectorComponent') chartTimeSelectorComponent?: ChartTimeSelectorComponent;
  isCreate: boolean = false;
  indexToEdit?: number;
  currentSelectedChartIndex: number = 0;
  dateIsValid: boolean = true;
  bucketIsValid: boolean = true;
  editButton: WidgetButton;
  createButton: WidgetButton;
  refreshButton: WidgetButton;
  protected readonly TypeOfDateSettingsEnum = TypeOfDateSettingsEnumCharts;
  protected popupOpen = false;
  protected saveRequestSent: boolean = false;
  /**
   * Contains the selected user metric group.
   * @protected
   */
  protected userMetricGroup?: UserMetricGroup;
  /**
   * Contains the selected user metric group with also extended info such as the metrics array.
   * @protected
   */
  protected fullUserMetricGroup?: UserMetricGroup;
  protected chartsToPlot: UserMetricGroup[] = [];
  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService = inject(DashboardService);
  /**
   * Service used for api calls related to users
   * @protected
   */
  protected userOpeService = inject(UserOpeService);

  constructor() {
    super();
    this.editButton = new WidgetButton();
    this.editButton.clickable = true;
    this.editButton.icon = "edit";
    this.editButton.editOnly = true;
    this.editButton.callbackFn = () => this.openEditPopup(false);

    this.createButton = new WidgetButton();
    this.createButton.clickable = true;
    this.createButton.icon = "add";
    this.createButton.editOnly = true;
    this.createButton.callbackFn = () => this.openEditPopup(true);

    this.widgetButtons = [];
    this.widgetButtons.push(this.createButton);

    this.refreshButton = new WidgetButton();
    this.refreshButton.clickable = true;
    this.refreshButton.icon = "arrow_sync";
    this.refreshButton.callbackFn = () => this.reloadCurrentChart();

    this.widgetButtons.push(this.refreshButton);

  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    if (!!this.gridsterItem.userConfig && !!this.gridsterItem.userConfig.chartsArray && this.gridsterItem.userConfig.chartsArray.length > 0) {
      this.chartsToPlot = this.gridsterItem.userConfig.chartsArray;
    } else {
      this.chartsToPlot = [];
    }
    this.hydrateChartsToPlotMetrics();
    this.checkWidgetMetricsExistence();
    this.initMetricGroup();
  }

  /**
   * chartsToPlot comes from gridsterItem.userConfig, which is raw JSON persisted by the backend and never
   * passed through the UserMetricInstance constructor. This hydrates each metric into a real UserMetricInstance
   * so class field defaults (e.g. isVisible) apply when the backend didn't persist them explicitly.
   */
  hydrateChartsToPlotMetrics(): void {
    this.chartsToPlot.forEach((chart) => {
      if (!!chart.metrics) {
        chart.metrics = chart.metrics.map(metric => Object.assign(new UserMetricInstance(), metric));
      }
    });
  }

  checkWidgetMetricsExistence(): void {
    if (!!this.gridsterItem.metricInstanceIds) {
      this.chartsToPlot.forEach((chart) => {
        if (!!chart.metrics) {
          const idsToDelete: number[] = [];
          chart.metrics?.forEach((metric, index) => {
            if (!this.gridsterItem.metricInstanceIds.includes(metric.id)) {
              idsToDelete.push(index);
            }
          });
          idsToDelete.sort((a, b) => b - a).forEach((id) => {
            chart.metrics!.splice(id, 1);
          })
        }
      });
    }
  }

  openEditPopup(isCreate: boolean, indexToEdit?: number) {
    this.popupOpen = true;
    this.isCreate = isCreate;
    if (indexToEdit !== undefined)
      this.indexToEdit = indexToEdit;
    else
      this.indexToEdit = this.currentSelectedChartIndex;
  }

  initMetricGroup() {
    if (!!this.fullUserMetricGroup) {
      if (!this.chartsToPlot.find(c => c.widgetId === this.fullUserMetricGroup!.widgetId))
        this.chartsToPlot.push(this.appService.deepCopy(this.fullUserMetricGroup));
      else {
        let idx = this.chartsToPlot.findIndex(c => c.widgetId === this.fullUserMetricGroup!.widgetId);
        if (idx >= 0) {
          this.chartsToPlot[idx] = this.appService.deepCopy(this.fullUserMetricGroup);
          this.currentSelectedChartIndex = idx;
        }
      }
    }
    this.chartsToPlot.forEach(
      (chart: UserMetricGroup) => {
        if (!chart.widgetId)
          chart.widgetId = uuid.v4();
      });
    if (this.chartsToPlot.length === 1)
      this.widgetButtons = [this.refreshButton, this.createButton, this.editButton];
    else
      this.widgetButtons = [this.refreshButton, this.createButton];
  }

  initForm(isCreate: boolean) {
    this.dashboardService.getUserMetricGroups().then(
      () => {
        if (isCreate) {
          delete this.fullUserMetricGroup;
          delete this.userMetricGroup;
        } else {
          if (this.indexToEdit !== undefined)
            if (this.chartsToPlot.length === 1)
              this.fullUserMetricGroup = this.appService.deepCopy(this.chartsToPlot[0]);
            else
              this.fullUserMetricGroup = this.appService.deepCopy(this.chartsToPlot[this.indexToEdit]);
          else
            this.fullUserMetricGroup = this.appService.deepCopy(this.chartsToPlot[this.currentSelectedChartIndex]);
          if (!!this.dashboardService.userMetricGroups.find(g => g.id === this.fullUserMetricGroup?.id))
            this.userMetricGroup = this.dashboardService.userMetricGroups.find(g => g.id === this.fullUserMetricGroup?.id);
        }
      }
    );
  }

  async checkForRealReload($event: any) {
    if (!this.userMetricGroup || $event.itemData.id !== this.userMetricGroup.id) {
      this.userMetricGroup = $event.itemData;
      let umgCB = new GetUserMetricGroupCB();
      umgCB.i.id = $event.itemData.id;
      await this.userOpeService.getUserMetricGroup(umgCB);
      let prevUuid = this.fullUserMetricGroup?.widgetId;
      this.fullUserMetricGroup = new UserMetricGroup(umgCB);
      this.fullUserMetricGroup.widgetId = prevUuid;
    }
  }

  getMetricInstanceIds(): number[] {
    let result: number[] = [];
    this.chartsToPlot.forEach(c => {
      if (!!c.metrics) {
        c.metrics.forEach(metric => {
          if (!result.includes(metric.id)) {
            result.push(metric.id);
          }
        })
      }
    });
    return result;
  }

  saveChart() {
    if (this.dateIsValid && this.bucketIsValid) {
      this.saveRequestSent = true;
      let dw = DashboardWidget.clone(this.gridsterItem);
      if (this.isCreate && !!this.fullUserMetricGroup) {
        this.fullUserMetricGroup.widgetId = uuid.v4();
        this.chartsToPlot.push(this.appService.deepCopy(this.fullUserMetricGroup));
      } else {
        let indexToChange = this.chartsToPlot.findIndex(c => !!this.fullUserMetricGroup && c.widgetId === this.fullUserMetricGroup!.widgetId);
        if (indexToChange >= 0) {
          this.chartsToPlot[indexToChange] = this.appService.deepCopy(this.fullUserMetricGroup)!;
        }
      }
      dw.userConfig = {chartsArray: this.chartsToPlot};

      dw.metricInstanceIds = this.getMetricInstanceIds();

      if (!!this.dashboardService.selectedDashboard)
        this.dashboardService.putDashboardWidget(this.dashboardService.selectedDashboard.id, dw).then(
          () => {
            this.initMetricGroup();
            this.saveRequestSent = false
          }
        ).catch(_ => this.saveRequestSent = false);
      this.popupOpen = false;
    } else {
      this.popupOpen = true;
    }

  }

  deleteChart(title: string, index: number) {
    this.showDeleteDialog(title).show().done(
      async (result: boolean) => {
        if (result) {
          this.chartsToPlot.splice(index, 1);

          let dw = DashboardWidget.clone(this.gridsterItem);
          dw.userConfig = {chartsArray: this.chartsToPlot};

          dw.metricInstanceIds = this.getMetricInstanceIds();

          if (!!this.dashboardService.selectedDashboard)
            this.dashboardService.putDashboardWidget(this.dashboardService.selectedDashboard.id, dw).then(
              () => {
                if (this.chartsToPlot.length === 1)
                  this.widgetButtons = [this.createButton, this.editButton];
                else
                  this.widgetOptions = [this.createButton];
              }
            );
        }
      }
    );
  }

  dispatchResizeEvent() {
    window.dispatchEvent(new Event('resize'));
  }

  reloadCurrentChart() {
    let selectedChartData = this.chartsToPlot[this.currentSelectedChartIndex];
    if (!!selectedChartData) {
      let selectedChartComponent = this.chartsComponents.find(
        chart => {
          return chart.userMetricGroup?.widgetId === selectedChartData.widgetId;
        }
      );
      if (!!selectedChartComponent) {
        selectedChartComponent.loadData();
      }
    }
  }


  exportCurrentChart() {
    let selectedChartData = this.chartsToPlot[this.currentSelectedChartIndex];
    if (!!selectedChartData) {
      let selectedChartComponent = this.chartsComponents.find(
        chart => {
          return chart.userMetricGroup?.widgetId === selectedChartData.widgetId;
        }
      );
      if (!!selectedChartComponent) {
        selectedChartComponent.parseAndDownloadChartDataForCsv();
      }
    }
  }

  checkDateRange() {
    if (!!this.chartTimeSelectorComponent)
      this.dateIsValid = this.chartTimeSelectorComponent.checkStartDateValidity();
  }

}
